import { PLATFORM_THEME_COLOR, resolveBrandTheme } from '@/lib/brand/resolve-brand-theme';

import { hexToOklch } from '@/lib/brand/oklch';

export { hexToOklch } from '@/lib/brand/oklch';

// The platform default hex, re-exported so reset-to-platform call sites keep one constant. This
// is the platform colour (#2563EB), not the old #0BA5EC sentinel: the sentinel comparison that
// silently un-branded a tenant picking that exact hex is gone (decision #6).
export const DEFAULT_THEME_COLOR = PLATFORM_THEME_COLOR;

type OKLCHColor = { l: number; c: number; h: number };

// Back-compat alias for call sites that still import the upper-case name; the derivation now
// lives in lib/brand/oklch and this re-export keeps one implementation.
export function hexToOKLCH(hex: string): OKLCHColor {
	return hexToOklch(hex);
}

// Imperative document-root apply, kept for chrome that themes the whole app (global/customer
// stores). It folds into resolveBrandTheme for one derivation and no longer carries the
// #0BA5EC sentinel: a non-null colour always brands (a tenant picking any hex keeps branding),
// null clears back to the platform palette. Scoped subtrees spread resolveBrandTheme onto their
// own data-brand-theme wrapper instead of calling this.
export function updateThemeColors(color: string | null) {
	const root = document.documentElement;

	if (!color) {
		root.style.removeProperty('--organization-primary');
		root.style.removeProperty('--organization-accent');
		root.style.removeProperty('--brand-color');
		root.style.removeProperty('--brand-color-foreground');
		root.style.removeProperty('--brand-color-hover');
		root.style.removeProperty('--brand-color-active');
		root.style.removeProperty('--brand-color-disabled');
		root.style.removeProperty('--brand-color-subtle');
		root.removeAttribute('data-brand-theme');
		return;
	}

	const oklch = hexToOklch(color);
	root.style.setProperty('--organization-primary', `oklch(${oklch.l}% ${oklch.c} ${oklch.h})`);
	root.style.setProperty('--organization-accent', `oklch(${oklch.l}% ${oklch.c} ${oklch.h} / 0.15)`);

	const vars = resolveBrandTheme({
		companyId: null,
		displayName: '',
		endorsement: null,
		faviconUrl: null,
		isBranded: true,
		logoUrl: null,
		themeColor: color,
		supportEmail: null,
		supportPhone: null,
	});
	for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value);
	root.setAttribute('data-brand-theme', '');
}

export const AVATAR_COLOR_MAP = {
	A: '#6D28D9', // Vibrant Purple
	B: '#2563EB', // Royal Blue
	C: '#059669', // Emerald
	D: '#8B5CF6', // Soft Purple
	E: '#0EA5E9', // Sky Blue
	F: '#10B981', // Fresh Green
	G: '#EC4899', // Bright Pink
	H: '#06B6D4', // Cyan
	I: '#F97316', // Warm Orange
	J: '#6366F1', // Indigo
	K: '#D946EF', // Light Purple
	L: '#0891B2', // Ocean Blue
	M: '#8B5CF6', // Violet
	N: '#059669', // Forest Green
	O: '#3B82F6', // Bright Blue
	P: '#14B8A6', // Teal
	Q: '#F59E0B', // Golden
	R: '#7C3AED', // Deep Purple
	S: '#0284C7', // Azure
	T: '#4F46E5', // Royal Indigo
	U: '#0D9488', // Sea Green
	V: '#9333EA', // Amethyst
	W: '#EA580C', // Sunset Orange
	X: '#0CA5E9', // Electric Blue
	Y: '#16A34A', // Spring Green
	Z: '#A855F7', // Lavender
} as { [key: string]: string };

export const AVATAR_FALLBACK_COLOR = '#64748B';

export const STAGE_FALLBACK_COLOR = '#94A3B8';

export function stageAccentFor({ color }: { color?: string | null }): string {
	return color ?? STAGE_FALLBACK_COLOR;
}

type AvatarColorStyle = {
	backgroundColor: string;
	color: string;
};

// Deterministic avatar tint keyed on the uppercased first letter of the name (first name first),
// matching the customer-portal palette. Non-letters or empty names fall back to a neutral slate.
export function avatarColorFor(name: string): AvatarColorStyle {
	const first = name.trim().charAt(0).toUpperCase();
	return { backgroundColor: AVATAR_COLOR_MAP[first] ?? AVATAR_FALLBACK_COLOR, color: '#ffffff' };
}
